import React, { useState, useContext, useEffect, useImperativeHandle, useRef } from "react";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { AccountContext } from "context/account";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiTagInput from "components/SuiTagInput";

import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

import toast from "react-hot-toast";
import api from "services/api";
import moment from "moment";

import { handleUploadFiles, getStyleImages, canResizeImageUrl } from "utils/imageHelper";
import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import UpStyledLogo from "components/UpStyled/UpStyledLogo";
import ReactPlayer from "react-player";
import Skeleton from "@mui/material/Skeleton";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";

/**
 * tutorial modal for user first time login
 */
const TutorialModal = React.forwardRef(
  (
    {
      visible,
      handleClose,
      handleOk,
      handleRefresh = () => {},
      handleAfterOk = () => {},
      refreshDelay = 0,
    },
    ref
  ) => {
    const [activeStep, setActiveStep] = useState(0);
    const [isHome, setIsHome] = useState(true);

    const Home = () => {
      return (
        <SuiBox
          width="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          py={4}
        >
          <SuiTypography variant="h2" color="text">
            Welcome to
          </SuiTypography>
          <SuiBox py={1.5} lineHeight={1}>
            <UpStyledLogo sx={{ height: 90 }} />
          </SuiBox>
          <SuiTypography variant="h5" color="dark" my={1} sx={{ fontWeight: "bold" }}>
            Bring your design ideas to life!
          </SuiTypography>

          <SuiButton
            color="dark"
            variant="contained"
            sx={{ my: 4 }}
            onClick={() => setIsHome(false)}
          >
            Explore UpStyled
          </SuiButton>

          <SuiTypography variant="body2" color="text">
            Not new to UpStyled Fashion?{" "}
            <a
              href=""
              onClick={(e) => {
                e.preventDefault();
                handleClose();
              }}
              style={{ color: "blue" }}
            >
              Skip the introduction
            </a>
          </SuiTypography>
        </SuiBox>
      );
    };

    const data = [
      {
        label: "Explore",
        title: "Share your work and receive crowd-sourced feedback from our community!",
        media: "https://upstyledus.oss-us-east-1.aliyuncs.com/www/videos/share-your-work2.mov",
        description: (
          <div style={{ textAlign: "center" }}>
            <SuiTypography variant="h6" sx={{ fontWeight: "regular" }}>
              Share your designs with <strong>Industry Insiders</strong>
            </SuiTypography>
            <SuiTypography variant="h6" sx={{ fontWeight: "regular" }}>
              to <strong>Generate Buzz</strong> and <strong>Receive Feedback</strong>
            </SuiTypography>{" "}
            <SuiTypography variant="h6" sx={{ fontWeight: "regular" }}>
              to improve your <strong>Sell-Through Rates.</strong>
            </SuiTypography>{" "}
          </div>
        ),
      },
      {
        label: "Create/AI Lab",

        title: "Bring your design ideas to life with the help of text-to-image AI generation!",
        media:
          "https://upstyledus.oss-us-east-1.aliyuncs.com/www/videos/bring-your-design-ideas.mov",
        description: (
          <div style={{ textAlign: "center" }}>
            <SuiTypography variant="h6" sx={{ fontWeight: "regular" }}>
              Experiment with Generative AI to assist with
            </SuiTypography>
            <SuiTypography variant="h6" sx={{ fontWeight: "regular" }}>
              your <strong>Moodboard Generation, Design</strong>
            </SuiTypography>{" "}
            <SuiTypography variant="h6" sx={{ fontWeight: "regular" }}>
              <strong>Customization, and Fabric/Trim</strong>
            </SuiTypography>{" "}
            <SuiTypography variant="h6" sx={{ fontWeight: "regular" }}>
              <strong>Experimentation.</strong>
            </SuiTypography>{" "}
          </div>
        ),
      },
      {
        label: "Create/Tech Packs",
        title: "Easily generate Tech Packs using our simple online tools!",
        media:
          "https://upstyledus.oss-us-east-1.aliyuncs.com/www/videos/easily-generate-tech-packs.mov",
        description: (
          <div style={{ textAlign: "center" }}>
            <SuiTypography variant="h6" sx={{ fontWeight: "regular" }}>
              Use our online tools to quickly generate
            </SuiTypography>
            <SuiTypography variant="h6" sx={{ fontWeight: "regular" }}>
              <strong>Factory-Ready Tech Packs.</strong> Want help from
            </SuiTypography>{" "}
            <SuiTypography variant="h6" sx={{ fontWeight: "regular" }}>
              a professional? Connect with our
            </SuiTypography>{" "}
            <SuiTypography variant="h6" sx={{ fontWeight: "regular" }}>
              <strong>Community of Fashion Specialists!</strong>
            </SuiTypography>{" "}
          </div>
        ),
      },
      {
        label: "My Projects",
        title: "Place production orders with a community of industry-leading Manufacturers!",
        media:
          "https://upstyledus.oss-us-east-1.aliyuncs.com/www/videos/place-production-orders.mov",
        description: (
          <div style={{ textAlign: "center" }}>
            <SuiTypography variant="h6" sx={{ fontWeight: "regular" }}>
              Ready to get your designs produced?
            </SuiTypography>
            <SuiTypography variant="h6" sx={{ fontWeight: "regular" }}>
              Submit a <strong>project</strong> to over <strong>400+ apparel</strong>
            </SuiTypography>{" "}
            <SuiTypography variant="h6" sx={{ fontWeight: "regular" }}>
              <strong>manufacturers </strong>and connect with the perfect
            </SuiTypography>{" "}
            <SuiTypography variant="h6" sx={{ fontWeight: "regular" }}>
              factory for your needs.
            </SuiTypography>{" "}
          </div>
        ),
      },
      {
        label: "Community/Connect",

        title: "Connect with trusted industry partners to grow your fashion network!",
        media: "https://upstyledus.oss-us-east-1.aliyuncs.com/www/videos/connect-with-trusted.mov",
        description: (
          <div style={{ textAlign: "center" }}>
            <SuiTypography variant="h6" sx={{ fontWeight: "regular" }}>
              Our platform community uses <strong>AI-Matching</strong>
            </SuiTypography>
            <SuiTypography variant="h6" sx={{ fontWeight: "regular" }}>
              to connect like-minded <strong>creatives &</strong>
            </SuiTypography>{" "}
            <SuiTypography variant="h6" sx={{ fontWeight: "regular" }}>
              <strong>manufacturers.</strong> We're home to over 400+
            </SuiTypography>{" "}
            <SuiTypography variant="h6" sx={{ fontWeight: "regular" }}>
              <strong>Manufacturers & Specialists who are</strong>
            </SuiTypography>{" "}
            <SuiTypography variant="h6" sx={{ fontWeight: "regular" }}>
              <strong>eager to connect with you!</strong>
            </SuiTypography>{" "}
          </div>
        ),
      },
    ];

    const handleNext = () => {
      if (activeStep < data.length - 1) {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
    };

    const handleBack = () => {
      if (activeStep > 0) {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
      }
    };

    return (
      <Dialog
        open={visible}
        onClose={(e, reason) => {
          if (reason != "backdropClick") {
            handleClose();
          }
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="lg"
        fullWidth={true}
      >
        <DialogContent>
          <IconButton
            aria-label="close"
            onClick={() => handleClose()}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          {isHome ? (
            <Home />
          ) : (
            <SuiBox>
              <Stepper activeStep={activeStep} alternativeLabel>
                {data.map((item, index) => {
                  return (
                    <Step key={index}>
                      <StepLabel sx={{ fontSize: 10 }}>{item?.label}</StepLabel>
                    </Step>
                  );
                })}
              </Stepper>

              <SuiBox
                width="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
              >
                <SuiTypography variant="h4" color="dark">
                  {data[activeStep].title}
                </SuiTypography>
                <SuiBox sx={{ py: 1, px: 15, width: "100%" }}>
                  {" "}
                  <ReactPlayer
                    controls={true}
                    url={data[activeStep].media}
                    height="auto"
                    width="100%"
                    fallback={
                      <Skeleton animation="wave" height={450} width="100%" variant="rectangular" />
                    }
                    playing={true}
                  />
                </SuiBox>
                {/* <SuiTypography variant="h6" sx={{ fontWeight: "regular" }} color="text">
                  {data[activeStep].description}
                </SuiTypography> */}
              </SuiBox>

              <SuiBox display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                <SuiButton
                  color="secondary"
                  onClick={() => (activeStep == 0 ? handleClose() : handleBack())}
                >
                  {activeStep == 0 ? "Maybe Later" : "Back"}
                </SuiButton>

                <SuiButton
                  color="info"
                  onClick={() => (activeStep == data.length - 1 ? handleClose() : handleNext())}
                >
                  {activeStep == data.length - 1 ? "Get Started!" : "Next"}
                </SuiButton>
              </SuiBox>
            </SuiBox>
          )}
        </DialogContent>
      </Dialog>
    );
  }
);

export default TutorialModal;
